<template>
  <div>

    <div class="title-section">
      <div class="display-1 font-weight-bold">{{meta.title}}</div>
      <div class="subheading">{{meta.subtitle}}</div>
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Assignment</div><v-divider class="div-after"/>
    <div class="body-1">Recreate the geometry of the following scene, which originates from Whitted's
      work on an improved illumination model using ray tracing, using some sort of 3D software! The goal
      of this assignment is simply to have a baseline for the placement of entities in the 3D scene that
      we'll seek to re-create with our own raytracer. 
    </div>
    <v-img class="u-image" :src="require(`../assets/updates/0/whitted.png`)"/>
      <div class="font-weight-medium">Whitted, 1980</div>
    <div class="body-1">In order to complete this assignment, I adjusted some of the primitive shapes
      and entities in an Unreal Engine 4 scene in order to get something similar to the image above.
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Recreation</div><v-divider class="div-after"/>
    <v-img class="u-image" :src="require(`../assets/updates/1/recreation.png`)"/>
      <div class="font-weight-medium">Recreation in UE4</div>
    <div class="body-1">Close, though we're missing the reflection/transmission effects. That's not
      a significant concern for the recreation, since we're really just looking for basic coordinates
      to outline our basic scene with.
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Sphere</div><v-divider class="div-after"/>
    <v-img class="u-image" :src="require(`../assets/updates/1/Left_Sphere.png`)"/>
      <div class="font-weight-medium">Left / Front Sphere</div>
    <v-img class="u-image" :src="require(`../assets/updates/1/Right_Sphere.png`)"/>
      <div class="font-weight-medium">Right / Back Sphere</div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Floor, Light and Camera</div><v-divider class="div-after"/>
    <v-img class="u-image" :src="require(`../assets/updates/1/Floor_Attributes.png`)"/>
      <div class="font-weight-medium">Floor</div>
    <v-img class="u-image" :src="require(`../assets/updates/1/Light_Transforms.png`)"/>
      <div class="font-weight-medium">Light Source</div>
    <v-img class="u-image" :src="require(`../assets/updates/1/Camera_Transforms.png`)"/>
      <div class="font-weight-medium">Camera</div>
    <div class="body-1">The camera in UE4 is defined using XYZ coordinates and a rotation, rather than
      a position, look-at and an up-direction. Looking at the scene, it appears the camera is pointing 
      at the end of the flat plane, which has the following coordinates: 
    </div>
    <v-img class="u-image" :src="require(`../assets/updates/1/lookat.png`)"/>
      <div class="font-weight-medium">The Look-at Coordinates</div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Coordinate System</div><v-divider class="div-after"/>
    <div class="body-1">Now these coordinates are all dandy, but we need to keep in mind that UE4
      implements a left-handed coordinate system where the z-axis is pointing up! 
      <a href="https://twitter.com/FreyaHolmer/status/644881436982575104">Here's a fun chart I found 
      online comparing this system to other 3D engines/APIs</a>. I'll definitely have to decide on a
      convention prior to implementation. I'm leaning towards OpenGL's right handed coordinate system,
      as that's what I know (and love?)
    </div>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Right-handed Scene Coordinates</div><v-divider class="div-after"/>
    <v-data-table :headers="coord_headers" :items="right_hand_coords" class="elevation-1" :rows-per-page-items="rows_options">
      <template slot="items" slot-scope="props">
        <td>{{ props.item.name }}</td>
        <td>{{ props.item.x }}</td>
        <td>{{ props.item.y }}</td>
        <td>{{ props.item.z }}</td>
      </template>
    </v-data-table>


    <v-divider class="spacer"/><div class="headline font-weight-bold">Sphere/Floor Sizing</div><v-divider class="div-after"/>
    <div class="body-1">
      The sphere sizes are fairly simple to determine; they should be unit spheres scaled up to 2.0f
      in each direction. The floor is a little more complicated to determine, since we're given the
      scale of a model, rather than the absolute size of said model. When we look at the mesh editor
      for the floor, we're given bounds of 1000 x 1000 (ignoring the thickness of the floor). Spheres 
      have bounds of 100 x 100 by comparison. As a result, I'd expect the sizing for the floor to be 
      <span class="font-weight-bold">2000 (z-axis) by 1500 (x-axis)</span>
    </div>


  </div>
</template>

<script>

export default {
  name: 'U_1',
  data() {
    return {
      coord_headers: [
        {text: 'Entity', sortable: false, align: 'center'},
        {text: 'X Coordinate', sortable: false, align: 'center'},
        {text: 'Y Coordinate', sortable: false, align: 'center'},
        {text: 'Z Coordinate', sortable: false, align: 'center'}
      ],
      right_hand_coords: [
        {name: 'Front Sphere', x: '-230.0', y: '340.0', z: '410.0'},
        {name: 'Back Sphere', x: '-80.0', y: '230.0', z: '570.0'},
        {name: 'Floor', x: '-60.0', y: '20.0', z: '920.0'},
        {name: 'Light Source', x: '20.0', y: '710.0', z: '-80.0'},
        {name: 'Camera Position', x: '-210', y: '342.0', z: '100.0'},
        {name: 'Camera Look-at', x: '-210.0', y: '50.0', z: '1930.0'},
      ],
      rows_options: [-1],
    };
  },
  computed: {
    meta() {
      return this.$store.state.updates[1];
    },
  }
};
</script>